import { useCallback } from 'react';
import type { DataGroup } from '@/features/DataGrid';
import type { NumberFilterParams } from '@/features/filters';
import { useAppDispatch } from '@/shared/model';
import type { TotalData } from '@/shared/ui';
import {
  getPage,
  getTreeData,
  getTotalData,
  getNumberFilterParams,
} from './slice';
import type { DataConfig, DataResponse, TreeDataResponse } from './types';

export interface DataFetchers {
  onGetRowsPage: (offset?: number) => Promise<DataResponse>;
  onGetTotalData: () => Promise<TotalData>;
  onGetTreeData: (dataGroups: DataGroup[]) => Promise<TreeDataResponse>;
  onGetMetricsMinMax: () => Promise<NumberFilterParams>;
}

export function useDataFetchers(config: DataConfig): DataFetchers {
  const dispatch = useAppDispatch();

  const onGetRowsPage = useCallback(
    (offset?: number) => {
      return dispatch(
        getPage({
          dimensions: config.dimensions,
          metric: config.metric,
          dateScale: config.dateScale,
          dateRange: config.dateRange,
          sorting: config.sorting,
          probabilisticAttribution: config.probabilisticAttribution,
          cohortAgeScale: config.cohortAgeScale,
          isForecasted: config.isForecasted,
          filters: config.filters,
          offset,
        }),
      );
    },
    [
      dispatch,
      config.dimensions,
      config.metric,
      config.dateScale,
      config.dateRange,
      config.cohortAgeScale,
      config.isForecasted,
      config.probabilisticAttribution,
      config.filters,
      config.sorting,
    ],
  );

  const onGetTotalData = useCallback(() => {
    return dispatch(
      getTotalData({
        dimensions: config.dimensions,
        metric: config.metric,
        dateRange: config.dateRange,
        dateScale: config.dateScale,
        isForecasted: config.isForecasted,
        cohortAgeScale: config.cohortAgeScale,
        filters: config.filters,
        probabilisticAttribution: config.probabilisticAttribution,
      }),
    );
  }, [
    dispatch,
    config.dimensions,
    config.metric,
    config.dateRange,
    config.dateScale,
    config.isForecasted,
    config.cohortAgeScale,
    config.filters,
    config.probabilisticAttribution,
  ]);

  const onGetTreeData = useCallback(
    (parentGroups: DataGroup[]) => {
      return dispatch(
        getTreeData({
          parentGroups,
          dimensions: config.dimensions,
          metric: config.metric,
          dateRange: config.dateRange,
          dateScale: config.dateScale,
          cohortAgeScale: config.cohortAgeScale,
          filters: config.filters,
          sorting: config.sorting,
          isForecasted: config.isForecasted,
          probabilisticAttribution: config.probabilisticAttribution,
        }),
      );
    },
    [
      dispatch,
      config.dimensions,
      config.metric,
      config.dateScale,
      config.dateRange,
      config.cohortAgeScale,
      config.sorting,
      config.filters,
      config.isForecasted,
      config.probabilisticAttribution,
    ],
  );

  const onGetMetricsMinMax = useCallback(() => {
    return dispatch(
      getNumberFilterParams({
        filterName: config.metric,
        dimensions: config.dimensions,
        filters: config.filters,
        dateRange: config.dateRange,
        dateScale: config.dateScale,
        cohortAgeScale: config.cohortAgeScale,
        probabilisticAttribution: config.probabilisticAttribution,
        isForecasted: config.isForecasted,
        dataGridViewType: config.dataGridViewType,
      }),
    );
  }, [
    dispatch,
    config.metric,
    config.dimensions,
    config.filters,
    config.dateRange,
    config.dateScale,
    config.cohortAgeScale,
    config.probabilisticAttribution,
    config.isForecasted,
    config.dataGridViewType,
  ]);

  return { onGetRowsPage, onGetTotalData, onGetTreeData, onGetMetricsMinMax };
}
